import { useEffect, useMemo, useRef, useState } from 'react';
import { Route, Routes, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useStatsigClient } from '@statsig/react-bindings';
import PropTypes from 'prop-types';

import {
  QUESTION_TYPES,
  QUESTION_TYPES_WITH_CROSS_TABS,
  readSurveyResponsesSummaryGPT,
} from '@api/instant_survey';
import {
  exportMaxDiffPrefShareData,
  exportRawData,
  exportSummaryData,
  exportStatXtabData,
  readContinuousSumResponses,
  readEssayResponses,
  readGlobalFilters,
  readGlobalFiltersV2,
  readGridResponses,
  readMaxDiffResponses,
  readRankResponses,
  readRawData,
  readRawDataV2,
  readSurveyResponses,
  readSurveyResponseCounts,
  // readVideoResponses,
} from '@api/survey_dashboard';
import { GATES } from '@constants';

import { exportPowerPoint } from './utilities/helpers';

import {
  ButtonText,
  DownloadIcon,
  getFormattedDate,
  IconButton,
  LoaderSkeleton,
  MinusIcon,
  OverlayPopover,
  PlusIcon,
  SaveSmartPeopleGroupIcon,
  ShareIcon,
  Tabs,
  toast,
  Toolbar,
} from '@utilities';

import Column from '../../../../layouts/Column';
import Details from './components/Details';
import InsightsDetails from './components/InsightsDetails';
import ModalShare from '../../../../components/ModalShare';
import ModalSurveyPeopleGroup from '../../../../components/ModalSurveyPeopleGroup';
import RawData from './components/RawData';
import Responses from './components/Responses';

import { SEARCH_PARAM_KEYS } from '../../utilities/constants';
import { DISPLAY_AS } from './components/Responses/utilities/helpers';

import styles from './_index.module.scss';

const SURVEY_DASHBOARD_TABS = [
  { id: 'details', label: 'Survey Details' },
  { id: 'responses', label: 'Survey Responses' },
  { id: 'raw-data', label: 'Raw Data' },
];

const Dashboard = ({
  details,
  insightsDocument,
  insightsType,
  publicToken,
  survey,
  surveyQuestions,
  surveyType,
}) => {
  const [displayAs, setDisplayAs] = useState(DISPLAY_AS.PERCENT);
  const [keyFindingsMap, setKeyFindingsMap] = useState({});
  const [globalFilters, setGlobalFilters] = useState();
  const [globalFiltersError, setGlobalFiltersError] = useState();
  const [isFiltersLoading, setIsFiltersLoading] = useState(true);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isRawDataLoading, setIsRawDataLoading] = useState(true);
  const [isResponseDataLoading, setIsResponseDataLoading] = useState(true);
  const [rawData, setRawData] = useState();
  const [rawDataLoadingError, setRawDataLoadingError] = useState();
  const [responseData, setResponseData] = useState();
  const [responseLoadingError, setResponseLoadingError] = useState();
  const [showModalShare, setShowModalShare] = useState(false);
  const [showModalSurveyPeopleGroup, setShowModalSurveyPeopleGroup] = useState(false);
  const [showResearchObjective, setShowResearchObjective] = useState(false);
  const [responseCounts, setResponseCounts] = useState(null);

  const downloadButtonRef = useRef();
  const filtersRequestController = useRef();
  const isVideoSurvey = surveyType?.id === 'video-survey-v3-instant-survey-type';
  const navigate = useNavigate();
  const rawDataRequestController = useRef();
  const responseRequestController = useRef();

  const { client } = useStatsigClient();
  const canCrossTabs = useMemo(() => {
    return surveyQuestions?.some(({ type }) => QUESTION_TYPES_WITH_CROSS_TABS.includes(type));
  }, [surveyQuestions]);
  const canPreferenceShare = useMemo(() => {
    return (
      surveyQuestions?.some(({ type }) => type === QUESTION_TYPES.MAX_DIFF.externalType) &&
      client.checkGate(GATES.MAXDIFF_PREFERENCE_ANALYSIS)
    );
  }, [client, surveyQuestions]);
  const canShowResearchObjectives = client.checkGate(GATES.RESEARCH_OBJECTIVES);

  let { survey_id: surveyId, tab_id: tabId } = useParams();
  const [searchParams] = useSearchParams();

  const compareByOptions = useMemo(
    () => [
      {
        label: 'Primary Segments',
        options: [
          { label: 'Overall', value: 'overall' },
          ...(globalFilters?.quotaGroups?.length > 1
            ? [{ label: 'Quota Group', value: 'quota_group' }]
            : []),
        ],
      },
      {
        label: 'Demographic Segments',
        options: globalFilters?.demographicFilters
          ?.filter(({ compareBy }) => compareBy)
          .map(({ id, label }) => ({
            label,
            value: id,
          })),
      },
    ],
    [globalFilters, survey]
  );
  const questionsRef = survey ? survey.questions : insightsDocument.questions;
  const selectedCompareBy = useMemo(() => {
    if (!globalFilters) return null;
    const compareByValue = searchParams.get(SEARCH_PARAM_KEYS.COMPARE_BY);
    if (!compareByValue)
      return globalFilters?.quotaGroups?.length > 1
        ? compareByOptions[0].options[1]
        : compareByOptions[0].options[0];

    return [...compareByOptions[0].options, ...compareByOptions[1].options].find(
      ({ value }) => value === compareByValue
    );
  }, [globalFilters, searchParams]);
  const selectedDemographicFilters = useMemo(() => {
    const searchParamsObject = Object.fromEntries(searchParams);

    return (
      Object.keys(searchParamsObject)
        // only include properties that are in the demographic filters
        .filter((key) => globalFilters?.demographicFilters.find(({ id }) => id === key))
        .reduce((acc, key) => {
          acc[key] = searchParamsObject[key]?.split(',');
          return acc;
        }, {})
    );
  }, [globalFilters, searchParams]);
  const selectedOptionIds = searchParams.get(SEARCH_PARAM_KEYS.QUESTION_OPTION)?.split(',') || [];
  const selectedQuotaGroups = useMemo(() => {
    return searchParams
      .get(SEARCH_PARAM_KEYS.QUOTA_GROUP)
      ?.split(',')
      .map((groupRef) =>
        globalFilters?.quotaGroups.find(
          ({ instantSurveyGroupRef }) => instantSurveyGroupRef === groupRef
        )
      )
      .filter(Boolean);
  }, [globalFilters, searchParams]);

  const exportQuery = useMemo(
    () => ({
      compare_by:
        selectedCompareBy?.value !== compareByOptions[0].options[0].value
          ? selectedCompareBy?.value
          : '',
      option_filter: selectedOptionIds.join(';'),
      quota_group_filter: selectedQuotaGroups
        ?.flatMap(({ quotaGroupRefs }) => quotaGroupRefs)
        .join(';'),
      ...Object.fromEntries(
        Object.entries(selectedDemographicFilters).map(([key, value]) => [key, value.join(';')])
      ),
    }),
    [
      selectedCompareBy,
      compareByOptions,
      selectedOptionIds,
      selectedQuotaGroups,
      selectedDemographicFilters,
    ]
  );

  useEffect(() => {
    // redirect to first tab if invalid tab id
    if (tabId && SURVEY_DASHBOARD_TABS.every(({ id }) => id !== tabId)) {
      const firstTabId = SURVEY_DASHBOARD_TABS[0].id;
      navigate(
        publicToken
          ? `/share/survey/${publicToken}/${insightsDocument.id}/${firstTabId}`
          : `/dashboard/survey/${insightsDocument.id}/${firstTabId}`,
        { replace: true }
      );
    }
  }, [tabId]);

  useEffect(() => {
    // Fetch GPT summaries and key findings when survey and compare-by selection change
    if (!survey?.id || !selectedCompareBy) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchGPTData = async () => {
      try {
        const gptData = await readSurveyResponsesSummaryGPT({
          signal,
          survey_id: survey.id,
        });

        // Set GPT data for key findings and response summaries
        setKeyFindingsMap(gptData);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.warn('[Dashboard] Failed to fetch GPT data:', error);
          setKeyFindingsMap({});
        }
      }
    };

    fetchGPTData();
    return () => controller.abort();
  }, [survey, selectedCompareBy]);

  useEffect(() => {
    getFilters();
    getRawData();

    return () => {
      filtersRequestController?.current?.abort();
      rawDataRequestController?.current?.abort();
    };
  }, [insightsDocument]);

  useEffect(() => {
    if (globalFilters) {
      getResponseData();
    }

    return () => responseRequestController?.current?.abort();
  }, [globalFilters, searchParams]);

  const getFilters = async () => {
    const controller = new AbortController();
    filtersRequestController.current = controller;

    try {
      if (isVideoSurvey) {
        setGlobalFilters(
          await readGlobalFiltersV2({
            publicToken,
            signal: controller.signal,
            surveyId,
          })
        );
      } else {
        setGlobalFilters(
          await readGlobalFilters({
            publicToken,
            signal: controller.signal,
            surveyId,
          })
        );
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        setGlobalFiltersError(error);
      }
    }

    setIsFiltersLoading(false);
  };

  const getRawData = async () => {
    const controller = new AbortController();
    rawDataRequestController.current = controller;
    setIsRawDataLoading(true);

    try {
      const params = { publicToken, signal: controller.signal, surveyId };
      const response = isVideoSurvey ? await readRawDataV2(params) : await readRawData(params);
      setRawData(response);
      setIsRawDataLoading(false);
    } catch (error) {
      if (!controller.signal.aborted) {
        setRawDataLoadingError(error);
      }
      setIsRawDataLoading(false);
    }
  };

  const getResponseData = async () => {
    responseRequestController?.current?.abort();
    const controller = new AbortController();
    responseRequestController.current = controller;

    setIsResponseDataLoading(true);
    setResponseCounts(null);
    setResponseData(null);
    setResponseLoadingError(null);

    const params = {
      publicToken,
      query: exportQuery,
      signal: controller.signal,
      surveyId,
    };
    try {
      const requests = [
        readSurveyResponseCounts(params),
        readContinuousSumResponses(params),
        readMaxDiffResponses(params),
        readEssayResponses(params),
        readGridResponses(params),
        readRankResponses(params),
        readSurveyResponses(params),
        // readVideoResponses(params),
      ];

      const [surveyResponseCounts, ...responseData] = await Promise.all(requests);
      setResponseCounts(surveyResponseCounts);

      const flattenedResponseData = responseData.flat();
      setResponseData(
        surveyQuestions.reduce((acc, question) => {
          acc[question.id] = flattenedResponseData.find(({ id }) => id === question.id);
          return acc;
        }, {})
      );

      setIsResponseDataLoading(false);
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error(error);
        setResponseLoadingError(error);
        setIsResponseDataLoading(false);
      }
    }
  };

  const handleExportPowerPoint = async () => {
    const surveyResponsesArray = responseData ? Object.values(responseData).filter(Boolean) : [];
    if (!surveyResponsesArray.length) {
      toast(
        'No survey response data available for PowerPoint export. Please ensure the survey responses have loaded.',
        { status: 'error' }
      );
      return;
    }

    toast('Generating PowerPoint, please wait...', { status: 'info' });
    try {
      await exportPowerPoint({
        details,
        displayAs,
        globalFilters,
        insightsDocument,
        keyFindingsMap,
        questionsRef,
        responseData,
        searchParams,
        selectedCompareBy,
        survey,
        surveyQuestions,
      });
      toast('PowerPoint file has been downloaded', { status: 'success' });
    } catch (error) {
      console.error('PowerPoint generation error:', error);
      toast('Failed to generate PowerPoint presentation: ' + error.message, { status: 'error' });
    }
  };

  const handleExportDownload = async (exportDownloadAPI) => {
    try {
      await exportDownloadAPI({ surveyId });
    } catch (error) {
      console.error('Error occurred while exporting data', error);
      toast('Error occurred while exporting data.', { status: 'error' });
    }
  };

  const areExportOpsDisabled = isRawDataLoading || isResponseDataLoading;
  const downloadTooltipText = areExportOpsDisabled
    ? 'Download will be available after report has finished processing'
    : 'Download data';
  const surveyPeopleGroupTooltipText = areExportOpsDisabled
    ? 'Save Survey People Group will be available after report has finished processing'
    : 'Save Survey People Group';

  const canCreateSurveyPeopleGroups = client.checkGate(GATES.CREATE_SURVEY_PEOPLE_GROUPS);

  const surveyTitle = survey?.title || insightsDocument.title;

  return (
    <>
      <div>
        <Toolbar
          tools={
            publicToken
              ? []
              : [
                  <IconButton
                    isDisabled={areExportOpsDisabled}
                    onClick={() => setIsPopoverOpen(true)}
                    ref={downloadButtonRef}
                    tooltip={downloadTooltipText}
                  >
                    <DownloadIcon />
                  </IconButton>,
                  <>
                    {canCreateSurveyPeopleGroups ? (
                      <IconButton
                        isDisabled={areExportOpsDisabled}
                        onClick={() => setShowModalSurveyPeopleGroup(true)}
                        tooltip={surveyPeopleGroupTooltipText}
                      >
                        <SaveSmartPeopleGroupIcon />
                      </IconButton>
                    ) : null}
                  </>,
                  <IconButton onClick={() => setShowModalShare(true)} tooltip={'Share'}>
                    <ShareIcon />
                  </IconButton>,
                  <OverlayPopover
                    isActive={isPopoverOpen}
                    onHide={() => setIsPopoverOpen(false)}
                    target={downloadButtonRef?.current}
                  >
                    <ul>
                      <li>
                        <button onClick={() => handleExportDownload(exportSummaryData)}>
                          <span>Summary Tables</span>
                        </button>
                      </li>
                      <li>
                        <button onClick={() => handleExportDownload(exportRawData)}>
                          <span>Raw Data</span>
                        </button>
                      </li>
                      {canCrossTabs && (
                        <li>
                          <button onClick={() => handleExportDownload(exportStatXtabData)}>
                            <span>Crosstabs & Stat Testing</span>
                          </button>
                        </li>
                      )}
                      {canPreferenceShare && (
                        <li>
                          <button onClick={() => handleExportDownload(exportMaxDiffPrefShareData)}>
                            <span>Preference Share</span>
                          </button>
                        </li>
                      )}
                      <li>
                        <button onClick={() => handleExportPowerPoint()}>
                          <span>PowerPoint</span>
                        </button>
                      </li>
                    </ul>
                  </OverlayPopover>,
                ]
          }
        >
          <h2>{surveyTitle}</h2>
        </Toolbar>
        {canShowResearchObjectives && survey?.researchObjectives?.trim() && (
          <div className={styles['dashboard-research-objectives-collapsible']}>
            {showResearchObjective ? (
              <>
                <p className={styles['dashboard-research-objectives']}>
                  {survey.researchObjectives}
                </p>
                <ButtonText onClick={() => setShowResearchObjective(false)}>
                  <MinusIcon />
                  <span>
                    Hide <strong>Objective</strong>
                  </span>
                </ButtonText>
              </>
            ) : (
              <ButtonText onClick={() => setShowResearchObjective(true)}>
                <PlusIcon />
                <span>
                  Show <strong>Objective</strong>
                </span>
              </ButtonText>
            )}
          </div>
        )}
        <div>
          <p>
            {survey && survey.issueDateTime ? (
              <>
                Survey fielded on{' '}
                <strong>{getFormattedDate(new Date(survey.issueDateTime))}</strong>
              </>
            ) : (
              <>
                Survey created on{' '}
                <strong>{getFormattedDate(new Date(insightsDocument.created_datetime))}</strong>
              </>
            )}
          </p>
          {survey?.groups?.length > 1 && (
            <p>
              <strong>{survey.groups.length}</strong> quota groups
            </p>
          )}
          <p>
            <strong>{details?.completes || insightsDocument.completed}</strong>{' '}
            {survey?.groups?.length > 1 ? 'total responses' : 'responses'}
          </p>
          <p>
            <strong>{questionsRef.length}</strong>{' '}
            {questionsRef.length === 1 ? 'question' : 'questions'}
          </p>
        </div>
        {showModalShare && (
          <ModalShare
            hasAllColleagues={false}
            hasFindColleagues={false}
            hasSharePublicly
            ids={[Number(surveyId)]}
            isSurvey
            setShowModalShare={setShowModalShare}
          />
        )}

        {showModalSurveyPeopleGroup && (
          <ModalSurveyPeopleGroup
            compareBy={
              selectedCompareBy?.value !== compareByOptions[0].options[0].value
                ? selectedCompareBy?.value
                : ''
            }
            demographicFilter={selectedDemographicFilters}
            globalFilters={globalFilters}
            optionFilter={selectedOptionIds}
            quotaGroupFilter={selectedQuotaGroups}
            sampleSize={responseCounts?.filteredResponses || 0}
            setShowModalSurveyPeopleGroup={setShowModalSurveyPeopleGroup}
            surveyId={Number(surveyId)}
            surveyQuestions={surveyQuestions}
            surveyTitle={surveyTitle}
            totalResponses={responseCounts?.totalResponses || 0}
          />
        )}
        <hr />
      </div>

      <Tabs
        routes={SURVEY_DASHBOARD_TABS.map(({ id, label }) => ({
          is_active: id === tabId,
          label,
          to: publicToken
            ? `/share/survey/${publicToken}/${surveyId}/${id}${window.location.search}`
            : `/dashboard/survey/${surveyId}/${id}${window.location.search}`,
        }))}
      />
      {tabId === 'details' && (
        <Column>
          {survey ? (
            <Details
              details={details}
              survey={survey}
              surveyQuestions={surveyQuestions}
              surveyType={surveyType}
            />
          ) : (
            <InsightsDetails
              details={details}
              survey={insightsDocument}
              surveyQuestions={surveyQuestions}
              surveyType={insightsType}
            />
          )}
        </Column>
      )}
      {tabId === 'responses' && (
        <Responses
          compareByOptions={compareByOptions}
          displayAs={displayAs}
          exportQuery={exportQuery}
          globalFilters={globalFilters}
          globalFiltersError={globalFiltersError}
          isDataLoading={isResponseDataLoading}
          isFiltersLoading={isFiltersLoading}
          isVideoSurvey={isVideoSurvey}
          onDisplayAsChange={setDisplayAs}
          responseData={responseData}
          responseError={responseLoadingError}
          selectedCompareBy={selectedCompareBy}
          survey={survey}
          surveyQuestions={surveyQuestions}
          surveyResponseSummariesMap={keyFindingsMap}
        />
      )}
      {tabId === 'raw-data' && (
        <RawData
          isRawDataLoading={isRawDataLoading}
          isTooMuchData={
            (details?.completes || insightsDocument.completed) > details?.rawDataRowLimit
          }
          rawData={rawData}
          rawDataLoadingError={rawDataLoadingError}
        />
      )}
    </>
  );
};

Dashboard.propTypes = {
  details: PropTypes.object,
  id: PropTypes.number,
  insightsDocument: PropTypes.object.isRequired,
  insightsType: PropTypes.object,
  publicToken: PropTypes.string,
  survey: PropTypes.object,
  surveyQuestions: PropTypes.arrayOf(PropTypes.object),
  surveyType: PropTypes.object,
};

export const DashboardSkeleton = () => (
  <LoaderSkeleton className={styles['dashboard-skeleton']} height={725}>
    <rect height="95" rx="4" ry="4" width="850" x="0" y="0" />
    <rect height="40" rx="4" ry="4" width="100%" x="0" y="105" />
    <rect height="30" rx="4" ry="4" width="300" x="0" y="155" />
    <rect height="165" rx="4" ry="4" width="100%" x="0" y="195" />
    <rect height="30" rx="4" ry="4" width="300" x="0" y="380" />
    <rect height="58" rx="4" ry="4" width="100%" x="0" y="420" />
    <rect height="58" rx="4" ry="4" width="100%" x="0" y="488" />
    <rect height="58" rx="4" ry="4" width="100%" x="0" y="556" />
  </LoaderSkeleton>
);

const DashboardWithTabs = (props) => {
  return (
    <Routes>
      <Route element={<Dashboard {...props} />} path=":tab_id" />
      {/* cover case when no tab is selected */}
      <Route element={<Navigate replace to={SURVEY_DASHBOARD_TABS[0].id} />} path="*" />
    </Routes>
  );
};

export default Object.assign(DashboardWithTabs, {
  Skeleton: DashboardSkeleton,
});
